import * as React from "react"
import { Flex, Button } from "@chakra-ui/react"
import {
  brandingSelector,
  productCheckoutPageSelector
} from "../../../../recoil/atoms/room"
import { userOnProceedToBuyScreenSelection } from "../../../../recoil/atoms/overlay"

import { useSetRecoilState, useRecoilValue } from "recoil"
import { usePictureInPicture } from "../../../../hooks/usePictureInPicture"
import { pipParamsSelector } from "../../../../recoil/atoms/pictureInPicture"
/* import { ConsoleLogger } from "../../../../util/logger" */
import { ProductConfirmButtonsProps } from "./types"
import useOnClickWithTouchHandler from "../../../../hooks/useOnClickWithTouchHandler"
import BadgeOverlayModal from "../components/BadgeOverlayModal"
import { useBrowserType } from "../../../../hooks/useBrowserType"
import { isBigPlayIconState } from "../../../../recoil/atoms/bigPlayIcon"
import { productOverlaySelector } from "../../../../recoil/atoms/product-overlay"
import { viewDependingLayoutAtom } from "../../../../recoil/atoms/view"
import useProductPageOverlay from "../../../../hooks/useProductPageOverlay"
import { toPixelString } from "../../../../util"

const _ProductConfirmButtons: React.FC<ProductConfirmButtonsProps> = ({
  selectedVariant,
  selectedQuantity,
  setSelectedVariant,
  setSelectedQuantity,
  setOptionsSelected,
  isSmallScreen,
  setLargeDropDown,
  defualtOptionsSelected,
  defualtVariant,
  isHistoryProduct,
  buttonsContainerRef
}) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const { variants, isProductDelete, checkoutUrl, channel } = useRecoilValue(
    productOverlaySelector
  )
  const { isMobile, isTablet, isLandscape } = useRecoilValue(
    viewDependingLayoutAtom
  )
  const isProductCheckoutPage = useRecoilValue(productCheckoutPageSelector)
  const setProductOverlay = useSetRecoilState(productOverlaySelector)
  const setPiPParams = useSetRecoilState(pipParamsSelector)
  const { exitPictureInPicture, openExternalUrl } = usePictureInPicture()
  const { isNativeAppBrowser } = useBrowserType()
  const setBigPlayIcon = useSetRecoilState(isBigPlayIconState)
  const setIsUserOnProceedToBuyScreen = useSetRecoilState(
    userOnProceedToBuyScreenSelection
  )
  const clearSelection = React.useCallback(() => {
    setSelectedQuantity(1)
    setOptionsSelected(defualtOptionsSelected)
    setSelectedVariant(defualtVariant)
    setLargeDropDown(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defualtOptionsSelected, defualtVariant])

  const [isProductDeleted, setProductDeleted] = React.useState(false)

  const { closeProductPageOverlay, openFullScreenProductPage } = useProductPageOverlay()

  const handleCancel = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation()
      exitPictureInPicture()
      setPiPParams({ isActivePiP: false })
      clearSelection()
      setProductOverlay({ isOpen: false })
      setIsUserOnProceedToBuyScreen(false)
      setProductDeleted(false)
      closeProductPageOverlay(null)

      event.stopPropagation()
    },
    [
      closeProductPageOverlay,
      clearSelection,
      exitPictureInPicture,
      setPiPParams,
      setProductOverlay,
      setIsUserOnProceedToBuyScreen
    ]
  )

  const onCheckout = React.useCallback(
    async e => {
      if (isProductDelete) {
        setProductDeleted(true)
        return
      }

      if (selectedVariant && !isProductCheckoutPage) {
        const url =
          "https://" +
          selectedVariant?.variantCheckoutUrl.replace(
            "productQuantity",
            selectedQuantity.toString()
          )

        openFullScreenProductPage({
          url,
          fallbackHandler: () => {
            closeProductPageOverlay(null) // move video to Main video window
            openExternalUrl(url)
          }
        })

        clearSelection()
        setProductOverlay({ isOpen: false })
      } else {
        openExternalUrl(checkoutUrl)
        clearSelection()
        setProductOverlay({ isOpen: false })
        setPiPParams({ isRedirected: true })
        //'https://www.amazon.com/dp/B086Z6LNVS?ref=vse_pfo_vwis'
      }

      if (isNativeAppBrowser) {
        handleCancel(e)
        setPiPParams({ isActivePiP: false, isRedirected: false })

        setBigPlayIcon(false)
      }
    },
    [
      isProductDelete,
      selectedVariant,
      isProductCheckoutPage,
      isNativeAppBrowser,
      selectedQuantity,
      openExternalUrl,
      clearSelection,
      setProductOverlay,
      checkoutUrl,
      handleCancel,
      setPiPParams,
      setBigPlayIcon,
      openFullScreenProductPage,
      closeProductPageOverlay
    ]
  )

  const onCancelHandlers = useOnClickWithTouchHandler(handleCancel)
  const onCheckoutHandlers = useOnClickWithTouchHandler(onCheckout)

  const isProductAvailable = variants.length === 0 || !!selectedVariant

  // Displayed Checkout text only for shopify product and shopify checkout url is active
  const checkOutText = channel !== "shopify" ? "Next" : isProductCheckoutPage ? "Next" : "Checkout"

  return (
    <>
      <Flex
        marginTop="auto"
        marginLeft="auto"
        onClick={e => e.stopPropagation()}
        ref={buttonsContainerRef}
      >
        <Button
          {...onCancelHandlers}
          /*  isDisabled={!isPrev} */
          colorscheme="teal"
          variant="outline"
          onClick={handleCancel}
          size={isSmallScreen && !isMobile && !isTablet ? "xs" : "sm"}
          fontSize={isSmallScreen && !isMobile && !isTablet ? "xs" : "sm"}
          padding={isMobile && "0px 10px"}
          height={
            isMobile ? "25px" : isSmallScreen || isTablet ? "24px" : "32px"
          }
          minH={isMobile && "25px"}
          style={{
            marginRight: "5px",
            alignSelf: "flex-end"
          }}
          _focus={{}}
          bg={
            roomStyles?.badgeButtonBackgroundColor
              ? roomStyles.badgeButtonBackgroundColor
              : "gray.100"
          }
          textColor={
            roomStyles?.badgeButtonTextColor
              ? roomStyles.badgeButtonTextColor
              : "#718096"
          }
          _hover={{
            backgroundColor: roomStyles?.badgeButtonMouseoverBackgroundColor
              ? roomStyles.badgeButtonMouseoverBackgroundColor
              : "#edf2f7",
            textColor: roomStyles?.badgeButtonMouseoverTextColor
              ? roomStyles.badgeButtonMouseoverTextColor
              : "#000",
            borderColor: roomStyles?.badgeButtonBorderMouseoverColor || null,
          }}
          borderColor={
            roomStyles?.badgeButtonBorderColor
              ? roomStyles.badgeButtonBorderColor
              : "#e2e8f0"
          }
          borderRadius={toPixelString(roomStyles?.badgeButtonBorderRadius)}
          fontFamily={
            roomStyles?.badgeButtonFontFamily
              ? roomStyles.badgeButtonFontFamily
              : "inherit"
          }
          opacity={0.75}
        >
          Back
        </Button>
        <Button
          {...onCheckoutHandlers}
          colorscheme="teal"
          onClick={onCheckout}
          disabled={!isProductAvailable}
          variant="outline"
          size={isSmallScreen && !isMobile && !isTablet ? "xs" : "sm"}
          fontSize={isSmallScreen && !isMobile && !isTablet ? "xs" : "sm"}
          padding={isMobile && "0px 10px"}
          height={
            isMobile ? "25px" : isSmallScreen || isTablet ? "24px" : "32px"
          }
          style={{ alignSelf: "flex-end" }}
          _focus={{}}
          bg={
            roomStyles?.badgeButtonBackgroundColor
              ? roomStyles.badgeButtonBackgroundColor
              : "gray.100"
          }
          textColor={
            roomStyles?.badgeButtonTextColor
              ? roomStyles.badgeButtonTextColor
              : "#718096"
          }
          _hover={{
            backgroundColor: roomStyles?.badgeButtonMouseoverBackgroundColor
              ? roomStyles.badgeButtonMouseoverBackgroundColor
              : "#edf2f7",
            textColor: roomStyles?.badgeButtonMouseoverTextColor
              ? roomStyles.badgeButtonMouseoverTextColor
              : "#000",
            borderColor: roomStyles?.badgeButtonBorderMouseoverColor || null
          }}
          borderColor={
            roomStyles?.badgeButtonBorderColor
              ? roomStyles.badgeButtonBorderColor
              : "#e2e8f0"
          }
          borderRadius={toPixelString(roomStyles?.badgeButtonBorderRadius)}
          fontFamily={
            roomStyles?.badgeButtonFontFamily
              ? roomStyles.badgeButtonFontFamily
              : "inherit"
          }
        >
          {isProductAvailable ? checkOutText : "Unavailable"}
        </Button>
      </Flex>
      {isProductDeleted && (
        <BadgeOverlayModal
          isMobile={isMobile}
          isLandscape={isLandscape}
          onCancel={handleCancel}
        />
      )}
    </>
  )
}

export const ProductConfirmButtons = React.memo(_ProductConfirmButtons)
